import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  People as PeopleIcon,
  Description as DescriptionIcon,
  AttachMoney as AttachMoneyIcon,
  PendingActions as PendingActionsIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface DashboardStats {
  totalClientes: number;
  totalOrcamentos: number;
  totalReceitaMes: number;
  orcamentosPendentes: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactElement;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
          }}
        >
          <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 'bold',
                color: color,
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              color: color,
              '& svg': {
                fontSize: { xs: '2rem', sm: '2.5rem' },
              },
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalClientes: 0,
    totalOrcamentos: 0,
    totalReceitaMes: 0,
    orcamentosPendentes: 0,
  });
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await apiService.getDashboardStats();
        setStats(data as DashboardStats);
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          mb: { xs: 2, sm: 3 },
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          fontWeight: 'bold',
        }}
      >
        Dashboard
      </Typography>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total de Clientes"
            value={stats.totalClientes}
            icon={<PeopleIcon />}
            color={theme.palette.primary.main}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Orçamentos"
            value={stats.totalOrcamentos}
            icon={<DescriptionIcon />}
            color={theme.palette.success.main}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Receita do Mês"
            value={formatCurrency(stats.totalReceitaMes)}
            icon={<AttachMoneyIcon />}
            color={theme.palette.warning.main}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pendentes"
            value={stats.orcamentosPendentes}
            icon={<PendingActionsIcon />}
            color={theme.palette.error.main}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: { xs: 'auto', md: '400px' } }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Resumo de Atividades
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Sistema de controle para tinturaria em funcionamento
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • {stats.totalClientes} clientes cadastrados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • {stats.totalOrcamentos} orçamentos criados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • {stats.orcamentosPendentes} orçamentos pendentes de aprovação
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: { xs: 'auto', md: '400px' } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              Ações Rápidas
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                → Novo Cliente
              </Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                → Novo Orçamento
              </Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                → Nova Fórmula
              </Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                → Relatórios
              </Typography>
            </Box>
          </Paper>
        </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;